import { createRef } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import type { ITrafficSituation } from '../../../../../models/traffic-situation.model';
import type { ILine } from '../../../../../models/line.model';
import { TrafficSituationHelper } from '../../../../../helpers/traffic-situation.helper';
import { TranslationService } from '../../../../../services/translation.service';
import { AnalyticsService } from '../../../../../services/analytics-service';
import LineListShowcase from '../../../../Shared/LineListShowcase';
import EpiserverHelper from '../../../../../helpers/episerver.helper';
import TrafficSituationSeverityIcon from '../../../components/TrafficSituationSeverityIcon';

function createLastUpdatedTimeText(trafficSituation: ITrafficSituation) {
  const today = DateTime.now().startOf('day');
  const yesterday = DateTime.now().startOf('day').minus({ days: 1 });
  const creationDate = trafficSituation.creationTime.startOf('day');
  if (creationDate.equals(today)) {
    return `${TranslationService.get('trafficsituationspage.lastupdatedtimeprefix')} ${TranslationService.get('common.today').toLocaleLowerCase()} ${trafficSituation.creationTime.toFormat('HH:mm')}`;
  }
  if (creationDate.equals(yesterday)) {
    return `${TranslationService.get('trafficsituationspage.lastupdatedtimeprefix')} ${TranslationService.get('common.yesterday').toLocaleLowerCase()} ${trafficSituation.creationTime.toFormat(
      'HH:mm'
    )}`;
  }
  return `${TranslationService.get('trafficsituationspage.lastupdatedtimeprefix')} ${trafficSituation.creationTime.toFormat('yyyy-MM-dd HH:mm')}`;
}

function createValidityTimeText(trafficSituation: ITrafficSituation) {
  let formatString = 'EEEE d MMMM';

  if ((trafficSituation.endTime && trafficSituation.startTime.year !== trafficSituation.endTime?.year) || (!trafficSituation.endTime && trafficSituation.startTime.year !== DateTime.now().year)) {
    formatString += ' yyyy';
  }

  return `${TranslationService.get('trafficsituationspage.validitytimeprefix')} ${trafficSituation.startTime.toFormat(formatString)} - ${
    trafficSituation.endTime?.toFormat(formatString) ?? TranslationService.get('trafficsituationspage.untilfurthernotice')
  }`;
}

function TrafficSituation({ trafficSituation }: { trafficSituation: ITrafficSituation }) {
  // VARIABLES
  const distinctLines: ILine[] = TrafficSituationHelper.getDistinctLines(trafficSituation);
  const lastUpdatedText = createLastUpdatedTimeText(trafficSituation);
  const validityTimeText = createValidityTimeText(trafficSituation);
  const trafficSituationRef = createRef<HTMLHeadingElement>();

  // === EVENT HANDLERS ===
  function handleShowMoreDetailsClick() {
    AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Klick på visa mer information', '');
  }

  return (
    <div className="traffic-situation-one-dot-one p-3 print-no-break" ref={trafficSituationRef}>
      <div>
        <div>
          <div className="d-flex justify-content-between mb-2">
            <h3 className="h4 d-flex align-items-center text-nowrap mb-0">
              <TrafficSituationSeverityIcon severity={trafficSituation.severity} className="me-1"></TrafficSituationSeverityIcon>
              <span className="text-wrap">{TranslationService.get(`trafficsituationspage.trafficsituation.${trafficSituation.severity}.long`)}</span>
            </h3>
          </div>
          {distinctLines.length > 0 && <LineListShowcase lines={distinctLines} />}
        </div>
        <div className="font-italic mt-2">{lastUpdatedText}</div>
        {!!trafficSituation.title && (
          <div className="mt-2">
            {trafficSituation.title}
            {EpiserverHelper.isInEditMode() && <span className="fw-bold"> ({trafficSituation.situationNumber})</span>}
          </div>
        )}
        {!!trafficSituation.description && <div className="mt-2">{trafficSituation.description}</div>}
        {!!validityTimeText && <div className="mt-2">{validityTimeText}</div>}
        {trafficSituation.relativeDetailsUrl && (
          <div className="mt-2">
            <a href={trafficSituation.relativeDetailsUrl} onClick={handleShowMoreDetailsClick}>
              {TranslationService.get('trafficsituationspage.linktodetailspagetext')}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default TrafficSituation;
